/*
 * Copyright (c) 2018, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

/**
 * Helper function that watches the authenticate state, then applies it
 * as a boolean (authenticated) as well as attaches the userinfo data.
 */

import axios from "axios";
import { InteractionStatus } from "@azure/msal-browser";
import config from "./.samples.config";

export const AUTH_REDIRECT_URL_KEY = "lmsCustomReports.authRedirectUrl";

function getCurrentSameOriginUrl() {
  return window.location.href;
}

function buildRedirectRequest(request) {
  const redirectStartPage = getCurrentSameOriginUrl();
  window.sessionStorage.setItem(AUTH_REDIRECT_URL_KEY, redirectStartPage);

  return {
    ...request,
    redirectStartPage
  };
}

function normalizeLoginId(loginId) {
  if (!loginId) {
    return loginId;
  }

  const trimmedLoginId = loginId.trim();
  const emailSeparatorIndex = trimmedLoginId.indexOf("@");

  return emailSeparatorIndex > 0
    ? trimmedLoginId.substring(0, emailSeparatorIndex)
    : trimmedLoginId;
}

async function checkAuthentication() {
  const { instance, accounts, inProgress } = this.props.msalContext;

  if (window.location.pathname === "/Logout" || inProgress !== InteractionStatus.None) {
    return;
  }

  if (!accounts.length) {
    await instance.loginRedirect(buildRedirectRequest(config.msal.loginRequest));
    return;
  }

  const account = instance.getActiveAccount() || accounts[0];
  instance.setActiveAccount(account);

  const authenticated = true;
  if (authenticated !== this.state.authenticated) {
    if (authenticated && !this.state.username) {
      const claims = account.idTokenClaims || {};
      const username = claims.name || account.name || account.username;
      const rawLoginId =
        claims.User_ID ||
        claims.LoginID ||
        claims.preferred_username ||
        claims.upn ||
        account.username;
      const loginId = normalizeLoginId(rawLoginId);
      const staffId =
        claims.staff_id || claims.employeeid || claims.employeeId || null;

      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...config.msal.tokenRequest,
          account
        });
        const token = tokenResponse.accessToken;
        this.props.storeCredential({
          authenticated,
          username,
          token,
          loginId,
          staffId
        });
        this.setState({
          authenticated,
          username,
          token,
          staffId
        });
      } catch (err) {
        await instance.loginRedirect(buildRedirectRequest({
          ...config.msal.tokenRequest,
          account
        }));
        return;
      }

      this.setState({ authenticated, username, staffId });
    } else {
      this.setState({ authenticated });
    }
  }
}

export function generateUUID() {
  // Public Domain/MIT
  var d = new Date().getTime(); //Timestamp
  var d2 = (performance && performance.now && performance.now() * 1000) || 0; //Time in microseconds since page-load or 0 if unsupported
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = Math.random() * 16; //random number between 0 and 16
    if (d > 0) {
      //Use timestamp until depleted
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/* eslint-disable import/prefer-default-export */
export { checkAuthentication };

export function createAndDownloadBlobFile(body, filename, extension = "pdf") {
  const blob = new Blob([body]);
  const fileName = `${filename}.${extension}`;
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, fileName);
  } else {
    const link = document.createElement("a");
    // Browsers that support HTML5 download attribute
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

export function downloadSignedURLFile(url) {
  const link = document.createElement("a");
  link.setAttribute("href", url);
  var userAgent = window.navigator.userAgent.toLowerCase();
  var ios = /iphone|ipod|ipad/.test(userAgent);
  if (ios === true) {
    link.setAttribute("target", "_blank");
  }
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function downloadSignedURLJsonFile(url) {
  const res = await axios({
    url: url,
    method: "GET",
    responseType: "application/json;charset=ANSI"
  })
    .then(response => {
      console.log("Signed Json downloaded successfully");

      return response;
    })
    .catch(error => {
      console.log("Error in downloading signed Json: " + error);
      return error;
    });

  return res;
}

export function handleExcelPdfErrorresponses(errorresponseObject) {
  var errorMessage = errorresponseObject.response
    ? errorresponseObject.response.status +
    (errorresponseObject.response.statusText !== ""
      ? " - " + errorresponseObject.response.statusText
      : " - " + errorresponseObject.response.data.data)
    : errorresponseObject.message;
  window.alert("Error in generating File, Details : " + errorMessage);
}

export function base64ToArrayBuffer(base64) {
  const binaryString = window.atob(base64); // Comment this if not using base64
  const bytes = new Uint8Array(binaryString.length);
  return bytes.map((byte, i) => binaryString.charCodeAt(i));
}

export const dayDifference = (a, b) => {
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor((utc1 - utc2) / _MS_PER_DAY);
}

export const calculateDaysOverdueDayPastDueDate = (data) => {
  const {
    assignStatus,
    dueDate,
    deCertificationDate,
    expirationDate,
    completionDate

  } = data;
  const today = new Date();
  const dueDateData = !!dueDate ? new Date(dueDate) : new Date();
  const dayDiffDueDate = dayDifference(today, dueDateData);

  const lastCompletiondate = !!completionDate ? new Date(completionDate) : new Date();
  const dayDiff_lastCompletiodate_dueDate = dayDifference(lastCompletiondate, dueDateData);
  // const daysOverdue = ['Overdue', 'Expired'].includes(assignStatus) ? dayDiff : 0;
  let daysOverdue = 0;
  let dayspastDuedate = 0;


  switch (assignStatus) {
    case 'Overdue':
    case 'Expired':
      daysOverdue = dayDiffDueDate;
      dayspastDuedate = dayDiffDueDate;
      break;
    case 'Decertified':
      const deCertificationDateData = !!deCertificationDate ? new Date(deCertificationDate) : new Date();
      const dayDiffDeCertificationDate = dayDifference(today, deCertificationDateData);
      console.log("deCertificationDate is ",typeof deCertificationDate,deCertificationDate);
      daysOverdue = dayDiffDeCertificationDate;
      dayspastDuedate = dayDiffDeCertificationDate;

      break;
    case 'Acquired':
      daysOverdue = 0;
      dayspastDuedate = 0;

      break;
    case 'Assigned':
      daysOverdue = 0;
      dayspastDuedate = !!dueDate ? 0 : "No due date specified";

      break;
    case 'Completed':
      daysOverdue = 0;
      dayspastDuedate = 0;

      break;
    case 'Registered/In progress':
      if (!!completionDate) {
        daysOverdue = 'N/A';
        dayspastDuedate = 'N/A';
      } else {
        daysOverdue = 0;
        dayspastDuedate = 0;
      }
      break;
    case 'Retraining Required':
      daysOverdue = 'N/A';
      dayspastDuedate = 'N/A';
      break;
    case 'In Recert':
      daysOverdue = 0;
      dayspastDuedate = 0;
      break;

    default:
      break;
  }

  return {
    daysOverdue,
    dayspastDuedate
  };

}


